'use client';

import { useMemo } from 'react';

import dynamic from 'next/dynamic';
import { useParams } from 'next/navigation';

import { Analytics, type BeforeSendEvent } from '@vercel/analytics/next';
import type { InitOptions } from '@kit/i18n';
import { ThemeProvider } from 'next-themes';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import { I18nextProvider } from 'react-i18next';

import { CaptchaProvider } from '@kit/auth/captcha/client';
import { useI18n } from '@kit/i18n/provider';
import { MonitoringProvider } from '@kit/monitoring/components';
import { PostHogProvider } from '@kit/product-analytics/posthog-provider';
import { AppEventsProvider } from '@kit/shared/events';
import { If } from '@kit/ui/if';
import { VersionUpdater } from '@kit/ui/version-updater';

import { AnalyticsProvider } from '~/components/analytics-provider';
import { AuthProvider } from '~/components/auth-provider';
import appConfig from '~/config/app.config';
import authConfig from '~/config/auth.config';
import featuresFlagConfig from '~/config/feature-flags.config';
import { i18nResolver } from '~/lib/i18n/i18n.resolver';
import { getI18nSettings } from '~/lib/i18n/i18n.settings';

import { ReactQueryProvider } from './react-query-provider';

const captchaSiteKey = authConfig.captchaTokenSiteKey;

const CaptchaTokenSetter = dynamic(async () => {
  if (!captchaSiteKey) {
    return Promise.resolve(() => null);
  }

  const { CaptchaTokenSetter } = await import('@kit/auth/captcha/client');

  return {
    default: CaptchaTokenSetter,
  };
});

export function RootProviders({
  lang,
  theme = appConfig.theme,
  children,
  
}: React.PropsWithChildren<{
  lang: string;
  theme?: string;
}>) {
  const i18nSettings = useMemo(() => getI18nSettings(lang), [lang]);
  const params = useParams();

  return (
    <MonitoringProvider>
      <AppEventsProvider>
        <PostHogProvider>
          <AnalyticsProvider>
            <ReactQueryProvider>
              <NuqsAdapter>
                <I18nProvider settings={i18nSettings} resolver={i18nResolver}>
                  <CaptchaProvider>
                    <CaptchaTokenSetter siteKey={captchaSiteKey} />

                    <AuthProvider>
                      <ThemeProvider
                        attribute="class"
                        enableSystem
                        disableTransitionOnChange
                        defaultTheme={theme}
                        enableColorScheme={false}
                      >
                        {children}
                        <Analytics
                          mode="production"
                          beforeSend={(event: BeforeSendEvent) => {
                            const url = new URL(event.url);
                            // Clear all search parameters
                            url.search = '';

                            // Replace dynamic segments with [id]
                            let pathname = url.pathname;
                            Object.values(params).forEach((param) => {
                              pathname = pathname.replace(
                                param as string,
                                '[id]',
                              );
                            });
                            url.pathname = pathname;

                            return {
                              ...event,
                              url: url.toString(),
                            };
                          }}
                        />
                      </ThemeProvider>
                  </AuthProvider>
                </CaptchaProvider>

                <If condition={featuresFlagConfig.enableVersionUpdater}>
                  <VersionUpdater />
                </If>
              </I18nProvider>
              </NuqsAdapter>
            </ReactQueryProvider>
          </AnalyticsProvider>
        </PostHogProvider>
      </AppEventsProvider>
    </MonitoringProvider>
  );
}

function I18nProvider({
  settings,
  resolver,
  children,
}: React.PropsWithChildren<{
  settings: InitOptions;
  resolver: (
    lang: string,
    namespace: string,
  ) => Promise<Record<string, string>>;
}>) {
  const i18n = useI18n(settings, resolver);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
